/**
 * GeolocationPositionProvider (#441 Phase 1) — the browser's own receiver,
 * behind the `PositionProvider` seam.
 *
 * This class holds every geolocation-specific concern that used to sit
 * inline in `useContextualNotams._startInternal`: the `navigator`
 * support check, the permission-denied short-circuit (Batch B #7), the
 * `watchPosition` arm with its config options, the `clearWatch` release,
 * and the narrowed arm-failure handling from PR #53 R2 finding #13 — a
 * SecurityError must not latch the loop as running.
 *
 * Behaviour is unchanged from the inline version, including the fact that
 * every arm failure ends at `setRuntimePermissionDenied()` exactly once.
 * That includes a browser with no geolocation API at all, which the
 * permission singleton models as `'unsupported'` rather than `'denied'` —
 * carried over deliberately, tracked separately rather than changed here,
 * because #441's AC 1 is that the real GPS path does not change.
 */
import { config } from '@/config/config';
import {
  setRuntimePermissionDenied,
  useGeolocationPermission
} from '@/composables/useGeolocationPermission';
import type { PositionProvider, PositionProviderKind } from './PositionProvider';

export class GeolocationPositionProvider implements PositionProvider {
  readonly kind: PositionProviderKind = 'geolocation';
  readonly fixesAge = true;

  /** The live watch, when one is armed. */
  private _watchId: number | null = null;
  /**
   * A watch we could not release because `navigator.geolocation` was gone
   * at the time (a WebView tears it down while backgrounded). Kept apart
   * from `_watchId` on purpose: it is NOT an armed source, so it must not
   * make `start()` think there is one — that combination left tracking
   * permanently dead, silently.
   */
  private _unreleasedWatchId: number | null = null;

  start(
    onPosition: (pos: GeolocationPosition) => void,
    onError: (err: GeolocationPositionError) => void
  ): boolean {
    // Already armed — a second watchPosition would orphan the first, and
    // both would keep delivering fixes with only one id left to clear.
    if (this._watchId !== null) return true;

    if (!('geolocation' in navigator)) {
      setRuntimePermissionDenied();
      return false;
    }

    // The API is back. Release anything a previous stop() could not, then
    // arm fresh — the old id belongs to a torn-down receiver, so a failure
    // here is expected and must not block the new watch.
    if (this._unreleasedWatchId !== null) {
      try {
        navigator.geolocation.clearWatch(this._unreleasedWatchId);
      } catch {
        // The receiver that owned it is gone; nothing left to release.
      }
      this._unreleasedWatchId = null;
    }
    const { permissionState } = useGeolocationPermission();
    if (permissionState.value === 'denied') {
      // Batch B #7 kept — no watchPosition arm. The permission singleton
      // is the source of truth for the permission_denied UI.
      setRuntimePermissionDenied();
      return false;
    }

    // Batch C #12 revisited (review R2 finding #13): the bare `catch {}`
    // swallowed every error kind. We still catch (so a SecurityError
    // doesn't latch the loop as running) but we (a) narrow to what the
    // browser actually throws and (b) surface a structured console.error
    // so field-debugging can see the actual cause.
    try {
      this._watchId = navigator.geolocation.watchPosition(onPosition, onError, {
        enableHighAccuracy: config.geolocation.enableHighAccuracy,
        maximumAge: config.geolocation.maximumAge,
        timeout: config.geolocation.timeout
      });
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[GeolocationPositionProvider] watchPosition arm failed', e);
      // Nothing to release: the throw happened before the assignment, so
      // no id was ever captured.
      // Classify as permission_denied so the VIEW can say so — since
      // #449 the tab stays and the route stays reachable; what this
      // changes is the placeholder the pilot reads. The console.error
      // above preserves the actual error kind for diagnosis (finding #13).
      setRuntimePermissionDenied();
      return false;
    }
  }

  stop(): void {
    if (this._watchId === null) return;
    const watchId = this._watchId;
    // Either way this provider is no longer armed, so the live id is
    // cleared unconditionally. A watch we could not release is PARKED, to
    // be cleared on the next successful arm — dropping it outright would
    // orphan it forever, and leaving it in `_watchId` would make start()
    // believe a dead watch is live.
    this._watchId = null;
    // A WebView can tear `navigator.geolocation` down while backgrounded.
    if (!('geolocation' in navigator)) {
      this._unreleasedWatchId = watchId;
      return;
    }
    try {
      navigator.geolocation.clearWatch(watchId);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[GeolocationPositionProvider] clearWatch failed', e);
      this._unreleasedWatchId = watchId;
    }
  }
}
